"use client"

import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { MdDarkMode, MdLightMode } from "react-icons/md"
import { flushSync } from "react-dom"
import { ThemeContext } from "../ThemeContext"

export const AnimatedThemeToggler = ({
    className = "",
    duration = 400,
    ...props
}) => {
    const { isLight, handleMode } = useContext(ThemeContext)
    const buttonRef = useRef(null)

    const toggleTheme = useCallback(async () => {
        if (!buttonRef.current) return

        // Check if View Transition API is supported
        if (!document.startViewTransition) {
            handleMode()
            return
        }

        await document.startViewTransition(() => {
            flushSync(() => {
                handleMode()
            })
        }).ready

        const { top, left, width, height } =
            buttonRef.current.getBoundingClientRect()
        const x = left + width / 2
        const y = top + height / 2
        const maxRadius = Math.hypot(
            Math.max(left, window.innerWidth - left),
            Math.max(top, window.innerHeight - top)
        )

        document.documentElement.animate(
            {
                clipPath: [
                    `circle(0px at ${x}px ${y}px)`,
                    `circle(${maxRadius}px at ${x}px ${y}px)`,
                ],
            },
            {
                duration,
                easing: "ease-in-out",
                pseudoElement: "::view-transition-new(root)",
            }
        )
    }, [handleMode, duration])

    return (
        <button
            ref={buttonRef}
            onClick={toggleTheme}
            className={`text-xl sm:text-2xl md:text-3xl cursor-pointer p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors ${className}`}
            aria-label="Toggle theme"
            {...props}
        >
            {isLight ? (
                <MdDarkMode className="dark:text-white text-zinc-900 w-6 h-6" />
            ) : (
                <MdLightMode className="dark:text-white text-zinc-900 w-6 h-6" />
            )}
            <span className="sr-only">Toggle theme</span>
        </button>
    )
}
